import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { GraduationCap, MapPin } from 'lucide-react';

interface EducationItem {
  degree: string;
  institution: string;
  location: string;
  period: string;
  description: string;
}

const educationData = {
  en: {
    title: 'Education',
    subtitle: 'My educational background and academic achievements.',
    education: [
      {
        degree: 'Bachelor of Science in Computer Science',
        institution: 'Faculty of Science, Alexandria University',
        location: 'Alexandria, Egypt',
        period: 'Sep 2017 - Jul 2021',
        description:
          'Graduated with Honors (Ranked 6th overall, 3.72 CGPA). Competed in the Egyptian Collegiate Programming Contest (ECPC) Qualifications and co-founded the Alex-Sci ICPC Community for training students in algorithms, data structures, and competitive problem solving.',
      },
      {
        degree: 'Front-End Development & UI Engineering',
        institution: 'Information Technology Institute (ITI)',
        location: 'Alexandria, Egypt',
        period: 'Aug 2020 - Sep 2020',
        description:
          'Intensive track covering modern frontend architectures, responsive interface engineering, and user-centered design principles.',
      },
    ],
  },
  ar: {
    title: 'التعليم',
    subtitle: 'خلفيتي التعليمية والإنجازات الأكاديمية.',
    education: [
      {
        degree: 'بكالوريوس علوم الحاسب',
        institution: 'كلية العلوم، جامعة الإسكندرية',
        location: 'الإسكندرية، مصر',
        period: 'سبتمبر 2017 - يوليو 2021',
        description:
          'تخرجت بمرتبة الشرف (الترتيب السادس على الدفعة، بمعدل 3.72 CGPA). التنافس في تصفيات مسابقة البرمجة المصرية الجامعية (ECPC)، والمشاركة في تأسيس مجتمع Alex-Sci ICPC لتدريب الطلاب على الخوارزميات وهياكل البيانات وحل المشكلات التنافسي.',
      },
      {
        degree: 'تطوير الواجهات الأمامية وهندسة تجربة المستخدم',
        institution: 'معهد تكنولوجيا المعلومات (ITI)',
        location: 'الإسكندرية، مصر',
        period: 'أغسطس 2020 - سبتمبر 2020',
        description:
          'برنامج مكثف ركّز على هندسة وتطوير واجهات الويب الحديثة وتطبيق مبادئ تجربة المستخدم وتصميم الأنظمة المتجاوبة.',
      },
    ],
  },
};

export function Education() {
  const { language, isRTL } = useLanguage();
  const data = educationData[language];

  const TimelineItem = ({ item }: { item: EducationItem }) => (
    <div className={`flex gap-6`}>
      <div className="flex flex-col items-center">
        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
          <GraduationCap className="w-6 h-6 text-white" />
        </div>
        <div className="w-px bg-gradient-to-b from-blue-600 to-purple-600 flex-1 mt-4"></div>
      </div>
      <div className="flex-1 pb-12">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
          <div className={`mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-lg font-bold text-white mb-1">{item.degree}</h3>
            <p className="text-blue-300 font-medium">{item.institution}</p>
            <div className={`flex items-center gap-4 mt-2 text-sm text-slate-300`}>
              <div className={`flex items-center gap-1`}>
                <MapPin className="w-4 h-4" />
                <span>{item.location}</span>
              </div>
              <span className="text-slate-400">•</span>
              <span>{item.period}</span>
            </div>
          </div>
          <p className={`text-slate-300 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
            {item.description}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <section id="education" className="py-20 px-4 sm:px-6 lg:px-8 relative -mt-20">
      <div className="max-w-4xl mx-auto">
        <div className={`text-center mb-16 ${isRTL ? 'text-right' : 'text-left'}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{data.title}</h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">{data.subtitle}</p>
        </div>

        <div className="space-y-8">
          {data.education.map((item, index) => (
            <TimelineItem key={index} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}
